import QRCode from "qrcode";

export type BrandedQROptions = {
  uid: string;
  itemName?: string;
  bottomText?: string;
  bottomText2?: string; // NEW
  bottomText3?: string; // NEW
  logoBase64?: string;
  qrSize?: number;
  logoSize?: number;
  fontSize?: number;
  fileName?: string;
  titleFont?: string;
  footerFont?: string;
  lineHeight?: number;
  padding?: number;
  textGap?: number;
};

const safeStr = (v: unknown) => (typeof v === "string" ? v : v == null ? "" : String(v));

function toDataUrlFromBase64(b64?: string): string | null {
  if (!b64) return null;
  const trimmed = b64.trim();
  if (trimmed.startsWith("data:image/")) return trimmed;
  return `data:image/png;base64,${trimmed}`;
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  if (!text) return [];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width <= maxWidth) {
      line = test;
    } else {
      if (line) lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

export async function generateBrandedQRDataUrl(opts: BrandedQROptions): Promise<string> {
  const {
    uid,
    itemName = "Item Name",
    bottomText = uid,
    bottomText2,
    bottomText3,
    logoBase64,
    qrSize = 500,
    logoSize = 100,
    fontSize = 24,
    titleFont = `700 ${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`,
    footerFont = `500 ${Math.max(12, fontSize - 4)}px system-ui, -apple-system, Segoe UI, Roboto, Arial`,
    lineHeight = 28,
    padding = 20,
    textGap = 12,
  } = opts;

  const DPR = Math.max(1, Math.floor(window.devicePixelRatio || 1));

  const title = safeStr(itemName);

  // Build ordered footer blocks (only include if provided/non-empty)
  const footerBlocks = [safeStr(bottomText), safeStr(bottomText2), safeStr(bottomText3)]
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

  const measure = document.createElement("canvas").getContext("2d")!;
  const maxTextWidth = qrSize - padding * 2;

  // Title measurement
  measure.font = titleFont;
  const titleLines = wrapText(measure, title, maxTextWidth);
  const titleHeight = title ? titleLines.length * lineHeight + textGap : 0;

  // Footer measurement (each block adds its lines + a textGap)
  measure.font = footerFont;
  const footerLinesList: string[][] = footerBlocks.map((t) => wrapText(measure, t, maxTextWidth));
  const footerHeights = footerLinesList.map((lines) =>
      lines.length > 0 ? lines.length * lineHeight + textGap : 0
  );
  const totalFooterHeight = footerHeights.reduce((a, b) => a + b, 0);

  const visibleWidth = qrSize;
  const visibleHeight = padding + titleHeight + qrSize + totalFooterHeight + padding;

  const canvas = document.createElement("canvas");
  canvas.width = visibleWidth * DPR;
  canvas.height = visibleHeight * DPR;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(DPR, DPR);

  // Background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, visibleWidth, visibleHeight);

  // Title
  let y = padding;
  if (title) {
    ctx.fillStyle = "#111827";
    ctx.font = titleFont;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    const cx = visibleWidth / 2;
    for (const line of titleLines) {
      ctx.fillText(line, cx, y);
      y += lineHeight;
    }
    // account for the inter-block gap (title block)
    y += textGap;
  }

  // QR code
  const qrCanvas = document.createElement("canvas");
  qrCanvas.width = qrSize;
  qrCanvas.height = qrSize;
  await QRCode.toCanvas(qrCanvas, safeStr(uid), {
    errorCorrectionLevel: "H",
    margin: 1,
    width: qrSize,
    color: { dark: "#000000", light: "#FFFFFF" },
  });

  const qrTop = padding + titleHeight;
  ctx.drawImage(qrCanvas, 0, qrTop);

  // Center logo over QR if provided
  const logoDataUrl = toDataUrlFromBase64(logoBase64);
  if (logoDataUrl) {
    const img = await new Promise<HTMLImageElement | null>((resolve) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => resolve(null);
      i.src = logoDataUrl;
    });

    if (img) {
      const ratio = Math.min(logoSize / img.width, logoSize / img.height, 1);
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);

      const logoX = Math.round(qrSize / 2 - w / 2);
      const logoY = Math.round(qrTop + qrSize / 2 - h / 2);

      const pad = 6;
      ctx.fillStyle = "#FFFFFF";
      drawRoundedRect(ctx, logoX - pad, logoY - pad, w + pad * 2, h + pad * 2, 10);
      ctx.fill();

      ctx.drawImage(img, logoX, logoY, w, h);
    }
  }

  // Footers (bottomText, bottomText2, bottomText3) in order
  if (footerLinesList.length > 0) {
    y = padding + titleHeight + qrSize + textGap;
    ctx.fillStyle = "#111827";
    ctx.font = footerFont;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    const cx = visibleWidth / 2;

    footerLinesList.forEach((lines, idx) => {
      for (const line of lines) {
        ctx.fillText(line, cx, y);
        y += lineHeight;
      }
      if (idx < footerLinesList.length - 1) {
        y += textGap;
      }
    });
  }

  return canvas.toDataURL("image/png");
}

export async function downloadBrandedQR(opts: BrandedQROptions): Promise<void> {
  const dataUrl = await generateBrandedQRDataUrl(opts);
  const link = document.createElement("a");
  const base =
      opts.fileName ??
      `${(opts.itemName || "Asset").replace(/\s+/g, "-")}-${opts.uid}`;
  link.href = dataUrl;
  link.download = `${base}.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}
